import { AuthorisedUser, Direction, OrderTerm, RepoType, SpreadsheetMapping, ValidationIssue } from 'api'
import { Locale, Translation } from 'locale'
import { isEmpty } from 'lodash'
import { OrderTermHelper } from 'model'
import { Nullable } from 'utils'
import {
  gatherIssues,
  getEnumMappings,
  parseDirection,
  parseEnum,
  parseNumber,
  parseRate,
  parseString,
  SpreadsheetParseResult,
  parseStringToArray,
  parseDate
} from '../upload/model'

interface AxeRawData {
  initiatorTradingEntity: any
  counterpartyOrganisations: any
  isinBui: any
  direction: any
  notional: any
  startConsideration: any
  rate: any
  term: any
  startDate: any
  endDate: any
  referenceRate: any
  repoType: any
}

export interface AxeData {
  initiatorTradingEntity: string
  counterpartyOrganisations: Nullable<string[]>
  isinBui: string
  direction: Nullable<Direction>
  notional: Nullable<number>
  startConsideration: Nullable<number>
  rate: Nullable<number>
  term: Nullable<OrderTerm>
  startDate: Nullable<Date>
  endDate: Nullable<Date>
  referenceRate: Nullable<string>
  repoType: Nullable<string>
}

export interface AxeParseResult extends SpreadsheetParseResult<AxeRawData> {
  pendingAxes: AxeData[]
}

interface AxeSpreadsheetParseRowResult {
  parseIssues: ValidationIssue[]
  axe: AxeData
}

export interface AxeSpreadsheetParseParams {
  rowData: AxeRawData[]
  filename: string
  spreadsheetMapping: SpreadsheetMapping
}

export const parseAxeSpreadsheetFactory = (authorisedUser: AuthorisedUser) => ({
  rowData: rows,
  filename,
  spreadsheetMapping
}: AxeSpreadsheetParseParams): AxeParseResult =>
  rows.reduce<AxeParseResult>(
    (r, row, idx) => {
      const { parseIssues, axe } = parseRow(row, idx + 1, spreadsheetMapping, authorisedUser)
      return {
        ...r,
        issues: [...r.issues, ...parseIssues],
        pendingAxes: isEmpty(parseIssues) ? [...r.pendingAxes, axe] : r.pendingAxes
      }
    },
    { filename, rows, issues: [], pendingAxes: [] }
  )

const parseRow = (
  row: any,
  rowNum: number,
  spreadsheetMapping: SpreadsheetMapping,
  authorisedUser: AuthorisedUser
): AxeSpreadsheetParseRowResult => {
  const initiatorTradingEntity = parseString(row, rowNum, spreadsheetMapping.fund, Locale.translate(Translation.TC_MY_ENTITY), true)
  const isinBui = parseString(row, rowNum, spreadsheetMapping.isin, Locale.translate(Translation.TC_ISIN_BUI), true)
  const counterpartyOrganisations = parseStringToArray(
    row,
    rowNum,
    spreadsheetMapping.counterparty,
    Locale.translate(Translation.TC_COUNTERPARTY),
    true
  )
  const direction = parseDirection(row, rowNum, spreadsheetMapping.direction, Locale.translate(Translation.TC_DIRECTION), true)
  const notional = parseNumber(row, rowNum, spreadsheetMapping.notional, Locale.translate(Translation.TC_NOTIONAL_SHORT))
  const startConsideration = parseNumber(
    row,
    rowNum,
    spreadsheetMapping.startConsideration,
    Locale.translate(Translation.TC_START_CONSIDERATION)
  )
  const referenceRate = parseString(row, rowNum, spreadsheetMapping.referenceRate, Locale.translate(Translation.TC_REFERENCE_RATE))
  const rate = parseRate(row, rowNum, spreadsheetMapping.rate, Locale.translate(Translation.TC_RATE), referenceRate)
  const term = parseEnum<OrderTerm>(
    row,
    rowNum,
    spreadsheetMapping.term,
    getEnumMappings(OrderTerm, OrderTermHelper),
    Locale.translate(Translation.TC_TERM)
  )

  const startDateWithUserDateFormat = { ...spreadsheetMapping.startDate, dateFormat: authorisedUser.userPreference.dateFormat }
  const startDate = parseDate(row, rowNum, startDateWithUserDateFormat, Locale.translate(Translation.TC_START_DATE))

  const endDateWithUserDateFormat = { ...spreadsheetMapping.endDate, dateFormat: authorisedUser.userPreference.dateFormat }
  const endDate = parseDate(row, rowNum, endDateWithUserDateFormat, Locale.translate(Translation.TC_END_DATE))
  const repoType = parseEnum<RepoType>(
    row,
    rowNum,
    spreadsheetMapping.repoType,
    spreadsheetMapping.repoType.values,
    Locale.translate(Translation.TC_REPO_TYPE)
  )

  return {
    parseIssues: gatherIssues(
      initiatorTradingEntity,
      isinBui,
      counterpartyOrganisations,
      direction,
      notional,
      startConsideration,
      referenceRate,
      rate,
      term,
      startDate,
      endDate,
      repoType
    ),
    axe: {
      initiatorTradingEntity: initiatorTradingEntity.isValue ? initiatorTradingEntity.value : '',
      counterpartyOrganisations: counterpartyOrganisations.isValue ? counterpartyOrganisations.value : null,
      isinBui: isinBui.isValue ? isinBui.value : '',
      direction: direction.isValue ? direction.value : null,
      notional: notional.isValue ? notional.value : null,
      startConsideration: startConsideration.isValue ? startConsideration.value : null,
      referenceRate: referenceRate.isValue ? referenceRate.value : null,
      rate: rate.isValue ? rate.value : null,
      term: term.isValue ? term.value : null,
      startDate: startDate.isValue ? startDate.value : null,
      endDate: endDate.isValue ? endDate.value : null,
      repoType: repoType.isValue ? repoType.value : null
    }
  }
}
